import React, { useState, useEffect } from 'react';
import { Users, UserPlus, UserMinus, Check, X, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FriendsManager = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('friends');

  useEffect(() => {
    fetchFriendsData();
  }, []); // Run once on mount

  const fetchFriendsData = async () => {
    setLoading(true);
    try {
      const [friendsRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/social/friends/list`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/social/friend-requests/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setFriends(friendsRes.data.friends || []);
      setPendingRequests(requestsRes.data);
    } catch (error) {
      console.error('Error fetching friends data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load friends data',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(
        `${API_URL}/api/social/friend-request/accept/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Friend Request Accepted',
        description: 'You are now friends!',
      });

      fetchFriendsData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to accept request',
      });
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.post(
        `${API_URL}/api/social/friend-request/reject/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Request Rejected',
        description: 'Friend request has been rejected',
      });

      fetchFriendsData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reject request',
      });
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/social/friends/remove/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Friend Removed',
        description: 'Friend has been removed from your list',
      });

      fetchFriendsData();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove friend',
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-400">
        Loading friends...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-400/10 to-purple-500/10 rounded-lg p-6 border border-blue-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">
              Friends & Connections
            </h3>
            <p className="text-gray-300">
              Manage your friends and friend requests
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-2 w-full bg-gray-900 mb-6">
          <TabsTrigger
            value="friends"
            className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-400 data-[state=active]:to-purple-500 data-[state=active]:text-white"
          >
            <Users className="w-4 h-4" />
            Friends ({friends.length})
          </TabsTrigger>
          <TabsTrigger
            value="requests"
            className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-400 data-[state=active]:to-purple-500 data-[state=active]:text-white"
          >
            <Clock className="w-4 h-4" />
            Requests ({pendingRequests.incoming?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Friends List */}
        <TabsContent value="friends" className="space-y-4">
          {friends.length === 0 ? (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">No friends yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Send friend requests to connect with other creators
                </p>
              </CardContent>
            </Card>
          ) : (
            friends.map((friend) => (
              <Card key={friend.id} className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                        {friend.username?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">
                          {friend.display_name || friend.username}
                        </h4>
                        <p className="text-sm text-gray-400">@{friend.username}</p>
                        {friend.role === 'admin' && (
                          <Badge className="mt-1 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                            Admin
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveFriend(friend.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <UserMinus className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Friend Requests */}
        <TabsContent value="requests" className="space-y-6">
          {/* Incoming Requests */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">
              Incoming Requests ({pendingRequests.incoming?.length || 0})
            </h4>
            {pendingRequests.incoming?.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-8 text-center">
                  <Clock className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No pending requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingRequests.incoming?.map((request) => (
                  <Card key={request.id} className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                            {request.from_user?.username?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">
                              {request.from_user?.display_name || request.from_user?.username}
                            </h4>
                            <p className="text-sm text-gray-400">
                              @{request.from_user?.username}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAcceptRequest(request.id)}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Accept
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRejectRequest(request.id)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing Requests */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">
              Sent Requests ({pendingRequests.outgoing?.length || 0})
            </h4>
            {pendingRequests.outgoing?.length === 0 ? (
              <Card className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-8 text-center">
                  <UserPlus className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">No sent requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingRequests.outgoing?.map((request) => (
                  <Card key={request.id} className="bg-gray-800/50 border-gray-700">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                            {request.to_user?.username?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h4 className="text-white font-semibold">
                              {request.to_user?.display_name || request.to_user?.username}
                            </h4>
                            <p className="text-sm text-gray-400">
                              @{request.to_user?.username}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          <Clock className="w-4 h-4 mr-1" />
                          Pending
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FriendsManager;
